<template>
    <div class="row full-width justify-center">
        <div v-if="!useBackupCodes" class="row q-gutter-x-sm no-wrap q-py-md">
            <q-input
                outlined
                dense
                v-for="i in length"
                v-model="fieldValues[i - 1]"
                @keyup="onKeyUp($event, i - 1)"
                @update:model-value="onUpdate($event, i - 1)"
                @paste="onPaste"
                :key="i"
                :ref="el => updateFieldRef(el, i - 1)"
                maxlength="1"
                input-class="text-center"
                class="otp-input"
                bg-color="grey-3"
                type="number"
                :autofocus="i === 1"
                :data-test="`otp-input-${i}`"
            />
        </div>
        <div v-else class="row">
            <q-input
                v-model="backupCodes"
                :maxlength="backupCodeLength"
                :label="$t('Backup code')"
                bg-color="grey-3"
                class="q-my-md"
                outlined
                dense
                style="font-size: 18px"
                autofocus
                color="accent"
            />
        </div>
        <div
            v-if="showBackupCodes"
            class="row full-width justify-center q-pb-md"
        >
            <!-- :label="$t('Use backup code')" -->
            <span
                class="cursor-pointer text-secondary q-px-sm"
                @click="useBackupCodes = !useBackupCodes"
            >
                {{ $t('Use backup code') }}
            </span>
            <q-toggle
                :model-value="useBackupCodes"
                size="xs"
                left-label
                color="accent"
                dense
                @update:model-value="toggleBackupCodes"
            />
        </div>
    </div>
</template>
<script>
export default {
    name: 'OneTimePasswordInput',
    emits: ['update:modelValue', 'passwordIsValid'],
    props: {
        length: {
            type: Number,
            default: 6
        },
        backupCodeLength: {
            type: Number,
            default: 12
        },
        showBackupCodes: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            fields: [],
            fieldValues: [],
            backupCodes: '',
            useBackupCodes: false
        };
    },
    // make sure to reset the refs before each update
    onBeforeUpdate() {
        this.fields = [];
    },
    watch: {
        combinedDigits: {
            handler(value) {
                this.$emit('update:modelValue', value);
                this.$emit('passwordIsValid', value.length === this.length);
            },
            immediate: true
        },
        backupCodes: {
            handler(value) {
                this.$emit('update:modelValue', value);
                this.$emit(
                    'passwordIsValid',
                    value.length === this.backupCodeLength
                );
            },
            immediate: true
        }
    },
    computed: {
        combinedDigits() {
            const nonNullFields = this.fieldValues.filter(value => value);
            if (this.length !== nonNullFields.length) {
                return '';
            }
            return nonNullFields.join('');
        }
    },
    methods: {
        toggleBackupCodes() {
            this.useBackupCodes = !this.useBackupCodes;
            this.backupCodes = '';
            this.fieldValues = [];
        },
        /**
         * Handle OTP input
         * @param {Element} element - The DOM element of the OTP input field
         * @param {Number} index - The index of the OTP input field
         */
        updateFieldRef(element, index) {
            if (element) {
                this.fields[index] = element;
            }
        },
        /**
         * Move focus to next OTP input field
         * @param {String} value - The value of the OTP input field
         * @param {Number} index - The index of the OTP input field
         */
        onUpdate(value, index) {
            if (value) {
                this.inputFocus(index + 1);
            }
        },
        /**
         * Handle user input on OTP input fields
         * @param {Event} e - The event object
         * @param {Number} index - The index of the OTP input field
         */
        onKeyUp(e, index) {
            const key = e.key;

            if (['Tab', 'Shift', 'Meta', 'Control', 'Alt'].includes(key)) {
                return;
            }

            if (['Delete'].includes(key)) {
                return;
            }

            if (key === 'ArrowLeft' || key === 'Backspace') {
                this.inputFocus(index - 1);
            } else if (key === 'ArrowRight') {
                this.inputFocus(index + 1);
            }
        },
        /**
         * Update focus on OTP input fields
         * @param {Number} index - The index of the OTP input field
         */
        inputFocus(index) {
            if (index >= 0) {
                if (index < this.length) {
                    this.fields[index].select();
                } else {
                    if (this.combinedDigits) {
                        this.fields[index - 1].blur();
                    }
                }
            }
        },
        /**
         * Handle paste event on OTP input fields
         *  @param {Event} event - The event object
         */
        onPaste(event) {
            const pasteData = event.clipboardData.getData('text').trim();

            // Validate the pasteData to ensure it's exactly 6 digits
            if (pasteData.length === this.length && /^\d+$/.test(pasteData)) {
                // Split the pasteData and populate the fieldValues array
                pasteData.split('').forEach((char, index) => {
                    if (index < this.length) {
                        this.fieldValues[index] = char;
                    }
                });

                // Move focus to the last input field
                this.$nextTick(() => {
                    this.inputFocus(this.length - 1);
                });

                event.preventDefault();
            }
        }
    }
};
</script>
<style lang="scss">
.otp-input {
    width: 4ch;
    font-size: 18px;

    input[type='number']::-webkit-outer-spin-button,
    input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    input[type='number'] {
        appearance: auto;
        -moz-appearance: textfield;
    }
}
</style>
